import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";

import { TourContext, type TourContextValue } from "./TourContext";
import { TourTargetsProvider } from "./TourTargetsProvider";
import type { TourDefinition } from "./types";

type TourProviderProps = {
  definition: TourDefinition;
  stepIds: string[];
  children: ReactNode;
};

type TourRun = { fromCanonical: number | null };

export function TourProvider({ definition, stepIds, children }: TourProviderProps) {
  const [run, setRun] = useState<TourRun | null>(null);
  const lastVisitedKey = useRef<string | null>(null);

  const start = useCallback(
    (fromCanonical?: number) => setRun({ fromCanonical: fromCanonical ?? null }),
    [],
  );
  const stop = useCallback(() => setRun(null), []);

  const value = useMemo<TourContextValue>(
    () => ({
      definition,
      stepIds,
      isRunning: run !== null,
      fromCanonical: run?.fromCanonical ?? null,
      start,
      stop,
      lastVisitedKey,
    }),
    [definition, stepIds, run, start, stop],
  );

  return (
    <TourTargetsProvider>
      <TourContext.Provider value={value}>{children}</TourContext.Provider>
    </TourTargetsProvider>
  );
}
